import React, { useState, useEffect } from "react";
import { FormButton, CancelButton } from "../../components/Botton";
import Loader from "../../components/Loader";
import { usePermissions, useAdminPermissions } from "./usePermissions";
import { IPermission } from "./permission.types";
import { CheckCircle, Security, ExpandMore, ExpandLess } from "@mui/icons-material";
import { toast } from "react-hot-toast";

interface AdminPermissionManagerProps {
  isOpen: boolean;
  onClose: () => void;
  admin: any;
}

// Define permission sections with their resources
const PERMISSION_SECTIONS = {
  "Content Management": [
    { name: "language", label: "Language Management", description: "Manage languages available in the system" },
    { name: "education", label: "Education Management", description: "Manage education standards and levels" },
    { name: "stream", label: "Stream Management", description: "Manage academic streams and courses" },
    { name: "subject", label: "Subject Management", description: "Manage subjects and their content" },
    { name: "test-series", label: "Test Series Management", description: "Manage test series and assessments" },
    { name: "question", label: "Question Management", description: "Manage questions and question banks" },
    { name: "ebook", label: "E-book Management", description: "Manage digital books and resources" },
    { name: "article", label: "Article Management", description: "Manage articles and news content" },
    { name: "article-category", label: "Article Categories", description: "Manage article categories and classification" }
  ],
  "User & System Management": [
    { name: "dashboard", label: "Dashboard Access", description: "View dashboard analytics and insights" },
    { name: "student", label: "Student Management", description: "Manage student accounts and data" },
    { name: "transaction", label: "Transaction Management", description: "View and manage payment transactions" },
    { name: "notification", label: "Notification System", description: "Send notifications to users" }
  ],
  "Website Content": [
    { name: "privacy-policy", label: "Privacy Policy", description: "Manage privacy policy content" },
    { name: "about-us", label: "About Us", description: "Manage about us page content" },
    { name: "help", label: "Help & Support", description: "Manage help documentation" },
    { name: "banner", label: "Banner Management", description: "Manage promotional banners" }
  ],
  "Commerce & Marketing": [
    { name: "coupon", label: "Coupon Management", description: "Manage discount coupons and offers" },
    { name: "coupon-category", label: "Coupon Categories", description: "Manage coupon categories and types" }
  ]
};

// Action labels and descriptions
const ACTION_LABELS = {
  read: { label: "View", description: "View and browse content", color: "blue" },
  create: { label: "Create", description: "Add new content", color: "green" },
  update: { label: "Edit", description: "Modify existing content", color: "orange" },
  delete: { label: "Delete", description: "Remove content", color: "red" },
  "enable-disable": { label: "Enable/Disable", description: "Enable or disable accounts", color: "purple" },
  send: { label: "Send", description: "Send notifications", color: "indigo" },
  "create-using-excel": { label: "Excel Import", description: "Import content via Excel", color: "teal" },
  coupon: { label: "Manage", description: "Full coupon management", color: "pink" }
};

const AdminPermissionManager: React.FC<AdminPermissionManagerProps> = ({
  isOpen,
  onClose,
  admin
}) => {
  const { permissions, loading: permissionsLoading } = usePermissions();
  const { assignPermissionsToAdmin, getAdminPermissions, loading: assignLoading } = useAdminPermissions();
  
  const [selectedPermissionIds, setSelectedPermissionIds] = useState<string[]>([]);
  const [expandedSections, setExpandedSections] = useState<{ [key: string]: boolean }>({});
  const [searchTerm, setSearchTerm] = useState("");
  const [isInitialized, setIsInitialized] = useState(false);

  const loading = permissionsLoading || assignLoading;

  // Initialize permissions when modal opens
  useEffect(() => {
    if (isOpen && admin && !isInitialized) {
      loadCurrentPermissions();
    }
  }, [isOpen, admin, isInitialized]);

  const loadCurrentPermissions = async () => {
    try {
      const adminPermissionsResponse = await getAdminPermissions(admin._id);
      console.log("Admin permissions response:", adminPermissionsResponse);
      
      // Handle different response structures from backend
      let currentPerms: IPermission[] = [];
      if (adminPermissionsResponse && 'permissions' in adminPermissionsResponse) {
        currentPerms = adminPermissionsResponse.permissions || [];
      }
      
      setSelectedPermissionIds(currentPerms.map((p: IPermission) => p._id));
      
      // Expand sections that have selected permissions
      const sectionsToExpand: { [key: string]: boolean } = {};
      Object.keys(PERMISSION_SECTIONS).forEach(sectionName => {
        const sectionResources = PERMISSION_SECTIONS[sectionName as keyof typeof PERMISSION_SECTIONS];
        const hasSelectedPermissions = sectionResources.some(resource =>
          currentPerms.some((p: IPermission) => p.resource === resource.name)
        );
        sectionsToExpand[sectionName] = hasSelectedPermissions;
      });
      setExpandedSections(sectionsToExpand);
      setIsInitialized(true);
    } catch (error: any) {
      console.error("Error loading admin permissions:", error);
      if (error.response?.status === 403) {
        toast.error("Access denied. Only Super Admins can manage permissions.");
        handleClose();
      } else {
        toast.error("Failed to load current permissions");
      }
    }
  };

  // Group permissions by resource
  const permissionsByResource = permissions.reduce((acc, permission) => {
    if (!acc[permission.resource]) {
      acc[permission.resource] = [];
    }
    acc[permission.resource].push(permission);
    return acc;
  }, {} as { [key: string]: IPermission[] });

  const toggleSection = (sectionName: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionName]: !prev[sectionName]
    }));
  };

  const handleSelectPermission = (permissionId: string) => {
    setSelectedPermissionIds(prev => {
      if (prev.includes(permissionId)) {
        return prev.filter(id => id !== permissionId);
      } else {
        return [...prev, permissionId];
      }
    });
  };

  const handleSelectAllForResource = (resource: string) => {
    const resourcePermissions = permissionsByResource[resource] || [];
    const resourceIds = resourcePermissions.map(p => p._id);
    const allSelected = resourceIds.every(id => selectedPermissionIds.includes(id));
    
    if (allSelected) {
      // Deselect all permissions for this resource
      setSelectedPermissionIds(prev => prev.filter(id => !resourceIds.includes(id)));
    } else {
      // Select all permissions for this resource
      setSelectedPermissionIds(prev => Array.from(new Set([...prev, ...resourceIds])));
    }
  };

  const handleSelectAllForSection = (sectionName: string) => {
    const sectionResources = PERMISSION_SECTIONS[sectionName as keyof typeof PERMISSION_SECTIONS];
    const sectionPermissionIds: string[] = [];
    
    sectionResources.forEach(resource => {
      const resourcePermissions = permissionsByResource[resource.name] || [];
      sectionPermissionIds.push(...resourcePermissions.map(p => p._id));
    });
    
    const allSelected = sectionPermissionIds.every(id => selectedPermissionIds.includes(id));
    
    if (allSelected) {
      // Deselect all permissions for this section
      setSelectedPermissionIds(prev => prev.filter(id => !sectionPermissionIds.includes(id)));
    } else {
      // Select all permissions for this section
      setSelectedPermissionIds(prev => Array.from(new Set([...prev, ...sectionPermissionIds])));
    }
  };

  // Bulk selection handlers
  const handleSelectAllPermissions = () => {
    const allPermissionIds = permissions.map(p => p._id);
    const allSelected = allPermissionIds.every(id => selectedPermissionIds.includes(id));
    
    if (allSelected) {
      setSelectedPermissionIds([]);
    } else {
      setSelectedPermissionIds(allPermissionIds);
    }
  };

  const handleSelectAllReadPermissions = () => {
    const readPermissionIds = permissions
      .filter(p => p.action === 'read')
      .map(p => p._id);
    
    const allReadSelected = readPermissionIds.every(id => selectedPermissionIds.includes(id));
    
    if (allReadSelected) {
      // Deselect all read permissions
      setSelectedPermissionIds(prev => prev.filter(id => !readPermissionIds.includes(id)));
    } else {
      // Select all read permissions
      setSelectedPermissionIds(prev => Array.from(new Set([...prev, ...readPermissionIds])));
    }
  };

  const handleClearAllPermissions = () => {
    setSelectedPermissionIds([]);
  };

  const getActionColor = (action: string) => {
    const actionInfo = ACTION_LABELS[action as keyof typeof ACTION_LABELS];
    return actionInfo?.color || "gray";
  };

  const getActionLabel = (action: string) => {
    const actionInfo = ACTION_LABELS[action as keyof typeof ACTION_LABELS];
    return actionInfo?.label || action;
  };

  const handleSubmit = async () => {
    try {
      await assignPermissionsToAdmin(admin._id, selectedPermissionIds);
      toast.success("Permissions updated successfully!");
      handleClose();
    } catch (error: any) {
      console.error("Error assigning permissions:", error);
      if (error.response?.status === 403) {
        toast.error("Access denied. Only Super Admins can manage permissions.");
        handleClose();
      } else {
        toast.error("Failed to update permissions");
      }
    }
  };

  const handleClose = () => {
    setSelectedPermissionIds([]);
    setExpandedSections({});
    setSearchTerm("");
    setIsInitialized(false);
    onClose();
  };

  if (!isOpen) return null;

  const selectedCount = selectedPermissionIds.length;
  const totalCount = permissions.length;

  // Filter sections based on search
  const filteredSections = Object.entries(PERMISSION_SECTIONS).filter(([sectionName, resources]) => {
    if (!searchTerm) return true;
    return sectionName.toLowerCase().includes(searchTerm.toLowerCase()) ||
           resources.some(resource => 
             resource.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
             resource.label.toLowerCase().includes(searchTerm.toLowerCase())
           );
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6">
          <div className="flex items-center gap-3 mb-2">
            <Security className="text-3xl" />
            <div>
              <h2 className="text-2xl font-bold">
                Manage Permissions
              </h2>
              <p className="text-blue-100 text-sm">
                {admin.name || admin.email}
              </p>
            </div>
          </div>
          <div className="flex items-center justify-between text-sm text-blue-100">
            <span>Configure access permissions for this administrator</span>
            <div className="bg-white bg-opacity-20 px-3 py-1 rounded-full">
              {selectedCount} of {totalCount} permissions selected
            </div>
          </div>
        </div>

        {loading && <Loader />}

        {/* Search and Controls */}
        <div className="p-6 border-b bg-gray-50">
          <div className="flex flex-col gap-4">
            <div className="flex gap-4 items-center">
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="Search sections or permissions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="text-sm text-gray-600">
                {selectedCount} selected
              </div>
            </div>
            
            {/* Bulk Selection Controls */}
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleSelectAllPermissions}
                className="px-3 py-1.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors"
              >
                Select All
              </button>
              <button
                type="button"
                onClick={handleSelectAllReadPermissions}
                className="px-3 py-1.5 text-xs font-medium bg-green-100 text-green-700 rounded-md hover:bg-green-200 transition-colors"
              >
                View All
              </button>
              <button
                type="button"
                onClick={handleClearAllPermissions}
                className="px-3 py-1.5 text-xs font-medium bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors"
              >
                Clear All
              </button>
            </div>
          </div>
        </div>

        {/* Permissions Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-4">
            {filteredSections.map(([sectionName, resources]) => {
              const isExpanded = expandedSections[sectionName];
              const sectionPermissionIds: string[] = [];
              
              resources.forEach(resource => {
                const resourcePermissions = permissionsByResource[resource.name] || [];
                sectionPermissionIds.push(...resourcePermissions.map(p => p._id));
              });
              
              const selectedInSection = sectionPermissionIds.filter(id => selectedPermissionIds.includes(id)).length;
              const totalInSection = sectionPermissionIds.length;
              const allSelected = selectedInSection === totalInSection && totalInSection > 0;
              const partiallySelected = selectedInSection > 0 && selectedInSection < totalInSection;

              return (
                <div key={sectionName} className="border border-gray-200 rounded-lg overflow-hidden">
                  {/* Section Header */}
                  <div 
                    className={`p-4 cursor-pointer transition-colors ${
                      isExpanded ? 'bg-blue-50 border-b border-gray-200' : 'bg-white hover:bg-gray-50'
                    }`}
                    onClick={() => toggleSection(sectionName)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectAllForSection(sectionName);
                          }}
                          className={`w-5 h-5 border-2 rounded flex items-center justify-center transition-colors ${
                            allSelected
                              ? 'bg-blue-600 border-blue-600 text-white'
                              : partiallySelected
                              ? 'bg-blue-100 border-blue-400'
                              : 'border-gray-300 hover:border-blue-400'
                          }`}
                        >
                          {allSelected && <CheckCircle className="w-3 h-3" />}
                          {partiallySelected && <div className="w-2 h-2 bg-blue-600 rounded-full" />}
                        </button>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">{sectionName}</h3>
                          <p className="text-sm text-gray-600">
                            {selectedInSection} of {totalInSection} permissions selected
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-500">
                          {resources.length} modules
                        </span>
                        {isExpanded ? <ExpandLess /> : <ExpandMore />}
                      </div>
                    </div>
                  </div>

                  {/* Section Content */}
                  {isExpanded && (
                    <div className="p-4 bg-white">
                      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                        {resources.map(resource => {
                          const resourcePermissions = permissionsByResource[resource.name] || [];
                          const selectedPermissions = resourcePermissions.filter(p => 
                            selectedPermissionIds.includes(p._id)
                          );
                          const allResourceSelected = resourcePermissions.length > 0 && 
                            resourcePermissions.every(p => selectedPermissionIds.includes(p._id));

                          return (
                            <div 
                              key={resource.name}
                              className={`p-4 border rounded-lg transition-colors ${
                                allResourceSelected 
                                  ? 'border-blue-500 bg-blue-50' 
                                  : selectedPermissions.length > 0
                                  ? 'border-blue-300 bg-blue-25'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              {/* Resource Header */}
                              <div className="flex items-start justify-between mb-3">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <button
                                      type="button"
                                      onClick={() => handleSelectAllForResource(resource.name)}
                                      className={`w-4 h-4 border-2 rounded flex items-center justify-center transition-colors ${
                                        allResourceSelected
                                          ? 'bg-blue-600 border-blue-600 text-white'
                                          : selectedPermissions.length > 0
                                          ? 'bg-blue-100 border-blue-400'
                                          : 'border-gray-300 hover:border-blue-400'
                                      }`}
                                    >
                                      {allResourceSelected && <CheckCircle className="w-2 h-2" />}
                                      {selectedPermissions.length > 0 && !allResourceSelected && 
                                        <div className="w-1.5 h-1.5 bg-blue-600 rounded-full" />
                                      }
                                    </button>
                                    <h4 className="font-medium text-gray-900 text-sm">
                                      {resource.label}
                                    </h4>
                                  </div>
                                  <p className="text-xs text-gray-600 mt-1">
                                    {resource.description}
                                  </p>
                                </div>
                              </div>

                              {/* Actions */}
                              <div className="space-y-2">
                                {resourcePermissions.map(permission => {
                                  const isSelected = selectedPermissionIds.includes(permission._id);
                                  const colorClass = getActionColor(permission.action);
                                  
                                  return (
                                    <label
                                      key={permission._id}
                                      className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                                        isSelected 
                                          ? `bg-${colorClass}-50 border border-${colorClass}-200` 
                                          : 'hover:bg-gray-50'
                                      }`}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={() => handleSelectPermission(permission._id)}
                                        className="rounded border-gray-300"
                                      />
                                      <span className={`px-2 py-1 rounded text-xs font-medium bg-${colorClass}-100 text-${colorClass}-800`}>
                                        {getActionLabel(permission.action)}
                                      </span>
                                    </label>
                                  );
                                })}
                                
                                {resourcePermissions.length === 0 && (
                                  <div className="text-xs text-gray-500 italic">
                                    No permissions defined
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t bg-gray-50">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              <span className="font-medium">{selectedCount}</span> of <span className="font-medium">{totalCount}</span> permissions selected
            </div>
            <div className="flex gap-3">
              <CancelButton
                type="button"
                onClick={handleClose}
                className="py-2 px-6 rounded-lg"
                children="Cancel"
              />
              <FormButton
                type="button"
                onClick={handleSubmit}
                disabled={loading}
                className="py-2 px-6 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium"
                children={loading ? "Saving..." : "Save Permissions"}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPermissionManager;